<template>
  <section class="fair-pay-promo">
    <div class="fair-pay-promo__hero">
      <div class="fair-pay-promo__hero-text">
        <p class="fair-pay-promo__badge">
          <span class="fair-pay-promo__badge-label">Fair Pay module</span>
          <span class="fair-pay-promo__badge-note">· Not in your plan</span>
        </p>
        <h1 class="fair-pay-promo__title">Pay people fairly, and show why</h1>
        <p class="fair-pay-promo__lead">
          Set market-based pay ranges for every position, keep each employee's pay plan with its
          full history, and see at a glance who is paid fairly and who is not.
        </p>
        <StandardButton
          class="fair-pay-promo__cta"
          type="magenta"
          variant="primary"
          size="xl"
          label="Learn more"
          :aria-label="LEARN_MORE_ARIA_LABEL"
          @click="openUserGuide"
        />
      </div>

      <div class="fair-pay-promo__preview" inert aria-hidden="true">
        <PayPlansTable :rows="FAIR_PAY_PROMO_PREVIEW_ROWS" />
      </div>
    </div>

    <div class="fair-pay-promo__features">
      <div class="fair-pay-promo__section-text">
        <h2 class="fair-pay-promo__section-title">Everything you need to pay fairly</h2>
        <p class="fair-pay-promo__lead">
          Pay ranges come from the market, pay plans carry the history, and fair pay is calculated
          for you.
        </p>
      </div>
      <ul class="fair-pay-promo__grid">
        <li v-for="feature in FEATURES" :key="feature.title" class="fair-pay-promo__feature">
          <h3 class="fair-pay-promo__feature-title">{{ feature.title }}</h3>
          <p class="fair-pay-promo__feature-text">{{ feature.text }}</p>
        </li>
      </ul>
    </div>

    <div class="fair-pay-promo__closing">
      <div class="fair-pay-promo__closing-text">
        <h2 class="fair-pay-promo__section-title">Want to see how Fair Pay works?</h2>
        <p class="fair-pay-promo__lead">
          The user guide walks through pay ranges, pay plans and fair pay step by step.
        </p>
      </div>
      <StandardButton
        class="fair-pay-promo__cta"
        type="magenta"
        variant="primary"
        size="xl"
        label="Learn more"
        :aria-label="LEARN_MORE_ARIA_LABEL"
        @click="openUserGuide"
      />
    </div>
  </section>
</template>

<script setup>
import PayPlansTable from "@components/PayPlansTable/PayPlansTable.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { FAIR_PAY_USER_GUIDE_URL } from "@components/constants/helpMenu";
import { FAIR_PAY_PROMO_PREVIEW_ROWS } from "./fairPayPromoPreviewRows.js";

const LEARN_MORE_ARIA_LABEL = "Learn more about Fair Pay in the user guide (opens in a new tab)";

const FEATURES = [
  {
    title: "Market-based pay ranges",
    text: "Create a pay range from salary surveys. IQ suggests a minimum and maximum, and you review them before saving.",
  },
  {
    title: "Versioned pay ranges",
    text: "Every change is a new version. See the range in force on any date and correct a mistake in place.",
  },
  {
    title: "Pay plans with history",
    text: "Each employee has a pay plan made of dated versions, shown on a timeline you can read at a glance.",
  },
  {
    title: "Proposals and approval",
    text: "Propose a pay change and send it for approval before it takes effect. Nothing changes silently.",
  },
  {
    title: "Pay ranges on positions",
    text: "Subscribe positions to pay ranges so every employee is compared with the right range.",
  },
  {
    title: "Fair pay, calculated",
    text: "See what fair pay is for each employee and how it compares with what they are paid. The calculation uses a proprietary formula.",
  },
];

function openUserGuide() {
  window.open(FAIR_PAY_USER_GUIDE_URL, "_blank", "noopener,noreferrer");
}
</script>

<style scoped>
.fair-pay-promo {
  width: 100%;
  background-color: #ffffff;
  font-family: "Inter", system-ui, -apple-system, sans-serif;
  color: #101828;
}

.fair-pay-promo__hero {
  display: flex;
  flex-direction: column;
  gap: 64px;
  padding: 96px 32px;
}

.fair-pay-promo__hero-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
}

.fair-pay-promo__badge {
  display: inline-flex;
  gap: 4px;
  margin: 0;
  padding: 4px 12px;
  border-radius: 16px;
  background-color: #f4ebff;
  color: #6941c6;
  font-size: 14px;
  line-height: 20px;
}

.fair-pay-promo__badge-label {
  font-weight: 500;
}

.fair-pay-promo__badge-note {
  font-weight: 400;
}

.fair-pay-promo__title {
  margin: 0;
  color: #101828;
  font-size: 60px;
  font-weight: 600;
  line-height: 72px;
}

.fair-pay-promo__lead {
  max-width: 768px;
  margin: 0;
  color: #475467;
  font-size: 20px;
  font-weight: 400;
  line-height: 30px;
}

.fair-pay-promo__preview {
  height: 520px;
  overflow: hidden;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  background-color: #ffffff;
  pointer-events: none;
  user-select: none;
}

.fair-pay-promo__features {
  display: flex;
  flex-direction: column;
  gap: 64px;
  padding: 96px 32px;
}

.fair-pay-promo__section-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
}

.fair-pay-promo__section-title {
  margin: 0;
  color: #101828;
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
}

.fair-pay-promo__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 64px 32px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.fair-pay-promo__feature {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.fair-pay-promo__feature-title {
  margin: 0;
  color: #101828;
  font-size: 20px;
  font-weight: 600;
  line-height: 30px;
}

.fair-pay-promo__feature-text {
  margin: 0;
  color: #475467;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.fair-pay-promo__closing {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  padding: 64px 32px;
  background-color: #f9fafb;
}

.fair-pay-promo__closing-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fair-pay-promo__cta {
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .fair-pay-promo__hero,
  .fair-pay-promo__features {
    padding: 64px 20px;
  }

  .fair-pay-promo__title {
    font-size: 40px;
    line-height: 48px;
  }

  .fair-pay-promo__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .fair-pay-promo__closing {
    flex-direction: column;
    align-items: flex-start;
    padding: 48px 20px;
  }
}
</style>
